import React from 'react';
import { Cell } from 'react-table';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { getOrderCancelReasons, orderSelector } from '../../../../store/slices';
import { NoData, SecondaryButton, Table } from '../../../components';
import { CancelReason } from '../../../../types';
import Modal from 'react-modal';
import { EditCancelReason } from './edit-cancel-reason';
import { backArrow } from '../../../../assets/svg';

// Column definitions for the cancel reasons table
const cancelReasonsColumns = [
  {
    Header: 'Reason',
    accessor: 'reason',
    Cell: ({ row }: Cell) => (
      <div className="font-medium text-gray-900">{row.values.reason}</div>
    ),
  },
  {
    Header: 'Refund Type',
    accessor: 'refundType',
    Cell: ({ row }: Cell) => {
      const refundType = row.values.refundType;
      const refundTypeLabels = {
        NO_REFUND: 'No Refund',
        FULL_REFUND: 'Full Refund',
        MEAL_ONLY: 'Product Refund Only',
      };

      const colorMap = {
        NO_REFUND: 'bg-red-100 text-red-800',
        FULL_REFUND: 'bg-green-100 text-green-800',
        MEAL_ONLY: 'bg-yellow-100 text-yellow-800',
      };

      return (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            colorMap[refundType as keyof typeof colorMap] ||
            'bg-gray-100 text-gray-800'
          }`}
        >
          {refundTypeLabels[refundType as keyof typeof refundTypeLabels] ||
            refundType}
        </span>
      );
    },
  },
  {
    Header: 'Auto Refund',
    accessor: 'autoRefund',
    Cell: ({ row }: Cell) => (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.values.autoRefund
            ? 'bg-blue-100 text-blue-800'
            : 'bg-gray-100 text-gray-800'
        }`}
      >
        {row.values.autoRefund ? 'Yes' : 'No'}
      </span>
    ),
  },
  {
    Header: 'Include Delivery',
    accessor: 'includeDelivery',
    Cell: ({ row }: Cell) => (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.values.includeDelivery
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
        }`}
      >
        {row.values.includeDelivery ? 'Yes' : 'No'}
      </span>
    ),
  },
  {
    Header: 'Include Service Fee',
    accessor: 'includeService',
    Cell: ({ row }: Cell) => (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.values.includeService
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
        }`}
      >
        {row.values.includeService ? 'Yes' : 'No'}
      </span>
    ),
  },
  {
    Header: 'Date Created',
    accessor: 'createdAt',
  },
  {
    Header: 'Edit',
  },
];

export const RefundConfigurations = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { cancelReasons, loading } = useAppSelector(orderSelector);

  const [modal, setModal] = React.useState('');
  const [selectedReason, setSelectedReason] =
    React.useState<CancelReason | null>(null);

  const handleEditReason = (
    reasonId: string,
    e: React.SyntheticEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    const reason = cancelReasons.find((r) => r.id === reasonId);
    if (reason) {
      setSelectedReason(reason);
      setModal('EDIT');
    }
  };

  const handleCreateReason = () => {
    setSelectedReason(null);
    setModal('CREATE');
  };

  const closeModal = () => {
    setModal('');
    setSelectedReason(null);
  };

  const refreshData = () => {
    dispatch(getOrderCancelReasons());
  };

  React.useEffect(() => {
    if (cancelReasons.length === 0) {
      dispatch(getOrderCancelReasons());
    }
  }, [dispatch, cancelReasons.length]);

  return (
    <>
      <div className="px-4 sm:px-8 py-8 w-full flex flex-col z-10 flex-grow">
        <button
          className="flex items-center space-x-2 text-sm relative z-10 mb-4"
          onClick={() => navigate(-1)}
        >
          <img src={backArrow} alt="back" />
          <span className="semibold">Back</span>
        </button>
        <div>
          <div className="header1">
            <h1 className="semibold text-xl">Cancel Reasons</h1>
            <div className="flex space-x-4 items-center">
              <SecondaryButton
                text="Add New Reason"
                onClick={handleCreateReason}
              />
            </div>
          </div>
        </div>

        <div className={`flex flex-col justify-between flex-grow mt-4`}>
          <Table
            columns={cancelReasonsColumns}
            data={cancelReasons}
            tableFor="cancelReasons"
            editMethod={handleEditReason}
            containerClassName="w-full"
          />
        </div>
      </div>

      {!loading && cancelReasons.length === 0 && (
        <NoData
          title="No cancel reasons"
          description="There are no cancel reasons configured"
          buttonText="Add New Reason"
          onclick={handleCreateReason}
          containerClassName="flex flex-col items-center flex-grow z-0"
        />
      )}

      <Modal
        isOpen={!!modal}
        onRequestClose={closeModal}
        ariaHideApp={false}
        className="lg:w-5/12 default-modal"
        overlayClassName="modal-overlay"
      >
        {(modal === 'EDIT' || modal === 'CREATE') && (
          <EditCancelReason
            reason={selectedReason}
            close={closeModal}
            refresh={refreshData}
          />
        )}
      </Modal>
    </>
  );
};
